import { axiosLevelTech } from './axiosConfig';
import { getLevelTechUrl, getWebAppUrl } from './admin_actions/apiUtils';
import { initTokens, setNativeToken } from './apiServiceCSRF';
import { useUserDataStore } from '@/store/userDataStore';
import { Browser } from '@capacitor/browser';
import { isMobileApp } from './mobile_app/mobileAppServices';
import { Preferences } from '@capacitor/preferences';

const CLIENT_ID = 'test-client-id-pkce-local'; 
const REDIRECT_URI = getWebAppUrl('/oauth/callback');

function base64URLEncode(str: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return await window.crypto.subtle.digest('SHA-256', data);
}

export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const hashed = await sha256(codeVerifier);
  return base64URLEncode(hashed);
}

function dec2hex(dec : number): string {
  return ("0" + dec.toString(16)).substring(-2);
}

function generateCodeVerifier() {
  var array = new Uint32Array(56 / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec2hex).join("");
}

export async function loginWithOAuth(): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  await Preferences.set({ key: 'pkce_code_verifier', value: codeVerifier });

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    code_challenge: codeChallenge,
    client_user_id: crypto.randomUUID(),
  });

  const url = `${getLevelTechUrl('/oauth/authorize')}?${params.toString()}`;
  if (isMobileApp()) {
    await Browser.open({ url, windowName: '_self' });
  } else {
    window.location.href = url;
  }
}

export async function exchangeCodeForToken(code: string): Promise<any> {
  const { value: codeVerifier } = await Preferences.get({ key: 'pkce_code_verifier' });
  if (!codeVerifier) throw new Error('Missing code verifier');

  const data = new URLSearchParams({
    auth_code: code,
    client_id: CLIENT_ID,
    code_verifier: codeVerifier
  });

  const response = await axiosLevelTech.post(getLevelTechUrl('/api/oauth/GetAccessToken'), data);
  if (response.data && response.data.data.access_token) {
    if (isMobileApp()) {
      await Preferences.set({ key: 'auth_bearer_token', value: response.data.data.access_token });
      await setNativeToken(response.data.data.access_token);
    }
    await initTokens();
    await useUserDataStore().fetchUserData();
  } else {
    console.error('Invalid token response:', response.data);
  }
  return response.data;
}

// Log out the user by clearing tokens and optionally notifying the backend
export async function logout(): Promise<void> {
  await Preferences.remove({ key: 'pkce_code_verifier' });
  await Preferences.remove({ key: 'auth_bearer_token' });
  // Optionally notify backend to revoke tokens
  try {
    await axiosLevelTech.post(getLevelTechUrl('/api/oauth/Logout'));
  } catch (e) {
    // Ignore errors for logout
  }
}

